/**
 * @puffin-core/auth-client — the MFA ENROLMENT leg, shared by the platform and the tenant services.
 *
 * Two sign-ins answer "enrol a second factor first" instead of a session, in ONE shape: 403 `MFA_SETUP_REQUIRED`
 * carrying `details.mfaEnrollmentToken`, a ten-minute token that only the app's MFA setup routes accept
 *   - the platform login, for a staff member with no second factor (Q-L16-98 · F-98-16);
 *   - the tenant sign-in, for a person whose MFA an administrator's reset cleared (owner ruling Q-ARG-1,
 *     "Required again", 2026-10-08).
 * So the reading of that refusal, and the two calls that resolve it (`/mfa/setup/init` with the password, then
 * `/mfa/setup/verify` with the first code, each with the enrolment token as an explicit one-off Bearer), live here
 * once. The services differ only in the session they persist and the user key they project (`staff` vs `user` +
 * `locations`), which each injects.
 *
 * @module @puffin-core/auth-client/mfaEnrollment
 */

import { extractData, authError, networkError } from './internal.js';

/** The sign-in refusal's code for an account that must enrol a second factor before any session. */
export const MFA_SETUP_REQUIRED_CODE = 'MFA_SETUP_REQUIRED';

/**
 * The `/mfa/setup/verify` refusal code for an account whose MFA is ALREADY enabled (`@puffin-core/auth-routes`, both
 * setup routers). It is the defined outcome of a completion replayed after its success response was lost: the
 * enrolment DID complete, so it is a state, not an error.
 */
export const MFA_ALREADY_ENABLED_CODE = 'MFA_ALREADY_ENABLED';

/**
 * The enrolment state a sign-in refusal carries, or null when the refusal is anything else.
 *
 * @param {{status: number, body: *}} res
 * @returns {({requiresMfaEnrollment: true, mfaEnrollmentToken: string, expiresInSeconds: number}|null)}
 */
export function readMfaEnrollmentRequired(res) {
  const error = res.body?.error;
  const token = error?.details?.mfaEnrollmentToken;
  if (res.status !== 403 || error?.code !== MFA_SETUP_REQUIRED_CODE || typeof token !== 'string') {
    return null;
  }
  return {
    requiresMfaEnrollment: true,
    mfaEnrollmentToken: token,
    expiresInSeconds: error.details.expiresInSeconds,
  };
}

/**
 * Build the begin / complete pair over one service's transport.
 *
 * @param {Object} deps
 * @param {Function} deps.request - the service's transport.
 * @param {string} deps.beginPath - `/mfa/setup/init` under the service's base.
 * @param {string} deps.completePath - `/mfa/setup/verify` under the service's base.
 * @param {Function} deps.persist - `(responseData) => void`: writes the minted session to the token store.
 * @param {Function} deps.project - `(responseData) => Object`: the user (and, for a tenant, locations) to return.
 * @param {string} deps.networkMessage - the service's network-failure wording.
 * @returns {{begin: Function, complete: Function}}
 */
export function buildMfaEnrollmentOperations({
  request,
  beginPath,
  completePath,
  persist,
  project,
  networkMessage,
}) {
  /**
   * Start the enrolment: the password is re-proved (one contract for every caller of `/mfa/setup/init`), and the
   * server answers the TOTP secret + otpauth URI.
   *
   * @param {string} mfaEnrollmentToken - from the sign-in refusal
   * @param {string} password
   * @returns {Promise<{secret: string, qrUri: string}>}
   */
  async function begin(mfaEnrollmentToken, password) {
    const res = await request({
      method: 'POST',
      path: beginPath,
      body: { password },
      auth: { token: mfaEnrollmentToken },
    });
    if (res.networkError) throw networkError(networkMessage);
    if (!res.ok) throw authError(res, 'MFA setup failed');
    const rd = extractData(res.body);
    return { secret: rd.secret, qrUri: rd.qrUri };
  }

  /**
   * Finish the enrolment with the first code: the server enables MFA and mints the session, which is persisted
   * exactly as a completed MFA challenge's is. A 409 `MFA_ALREADY_ENABLED` answers the `{ alreadyEnrolled: true }`
   * STATE instead of throwing (a lost success response); nothing is persisted and the person signs in again. Every
   * other refusal throws.
   *
   * @param {string} mfaEnrollmentToken
   * @param {string} code - the first TOTP code
   * @returns {Promise<(Object|{alreadyEnrolled: true})>} the projected session + `backupCodes`, or the state.
   */
  async function complete(mfaEnrollmentToken, code) {
    const res = await request({
      method: 'POST',
      path: completePath,
      body: { code },
      auth: { token: mfaEnrollmentToken },
    });
    if (res.networkError) throw networkError(networkMessage);
    if (res.status === 409 && res.body?.error?.code === MFA_ALREADY_ENABLED_CODE) {
      return { alreadyEnrolled: true };
    }
    if (!res.ok) throw authError(res, 'MFA setup failed');
    const rd = extractData(res.body);
    persist(rd);
    return { ...project(rd), backupCodes: rd.backupCodes };
  }

  return { begin, complete };
}
